import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  Restaurant as RestaurantIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Directions as DirectionsIcon,
  ShoppingCart as TakeawayIcon,
  List as ListIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import TakeawayView from './TakeawayView';
import { apiService, Location } from '../services/api';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Language translations
const translations = {
  nl: {
    reserveNow: 'Reserveer nu',
    takeaway: 'Takeaway',
    heroTitle: 'Reserveer jouw tafel in 2 klikken',
    heroSubtitle: 'Traditionele kipgerechten en Belgische specialiteiten',
    reviews: 'reviews',
  },
  fr: {
    reserveNow: 'Réserver maintenant',
    takeaway: 'À emporter',
    heroTitle: 'Réservez votre table en 2 clics',
    heroSubtitle: 'Plats de poulet traditionnels et spécialités belges',
    reviews: 'avis',
  },
  en: {
    reserveNow: 'Book now',
    takeaway: 'Takeaway',
    heroTitle: 'Book your table in 2 clicks',
    heroSubtitle: 'Traditional chicken dishes and Belgian specialties',
    reviews: 'reviews',
  }
};

const GuestReservationApp: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [language, setLanguage] = useState<'nl' | 'fr' | 'en'>('nl');
  const t = translations[language];
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [showTakeaway, setShowTakeaway] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [clickedLocation, setClickedLocation] = useState<Location | null>(null);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationLocation, setReservationLocation] = useState<Location | null>(null);
  const [timeSlotDialogOpen, setTimeSlotDialogOpen] = useState(false);
  const [mapZoom, setMapZoom] = useState(7);
  const [selectedDate, setSelectedDate] = useState('');
  const [reservationForm, setReservationForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    date: '',
    time: '18:00',
    guests: 2,
    notes: ''
  });
  const [showMapOnMobile, setShowMapOnMobile] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([50.8503, 4.3517]); // Belgium center
  const mapRef = useRef<any>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  // Effect to handle map movement when center or zoom changes
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setView(mapCenter, mapZoom);
    }
  }, [mapCenter, mapZoom]);

  // Load locations from API
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const data = await apiService.getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, []);

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLocationClick = (location: Location) => {
    setClickedLocation(location);
    setLocationDialogOpen(true);
    // Zoom map to clicked location
    setMapCenter(location.coordinates);
    setMapZoom(15);
  };

  const handleReservation = (location: Location) => {
    setReservationLocation(location);
    setTimeSlotDialogOpen(true);
    setLocationDialogOpen(false);
  };

  const handleTakeaway = (location: Location) => {
    setSelectedLocation(location);
    setShowTakeaway(true);
    setLocationDialogOpen(false);
  };

  const handleMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          console.log('Current location:', { lat: userLat, lng: userLng });
          
          // Center map on user location
          setMapCenter([userLat, userLng]);
          setMapZoom(12);
          
          // Find nearest Poule&Poulette location
          let nearestLocation: Location | null = null;
          let shortestDistance = Infinity;
          
          locations.forEach(location => {
            const distance = calculateDistance(
              userLat, userLng,
              location.coordinates[0], location.coordinates[1]
            );
            
            if (distance < shortestDistance) {
              shortestDistance = distance;
              nearestLocation = location;
            }
          });
          
          if (nearestLocation) {
            const distanceKm = (shortestDistance / 1000).toFixed(1);
            // Show the nearest location in the dialog
            setClickedLocation(nearestLocation as Location);
            setLocationDialogOpen(true);
            // Add distance info to the location temporarily
            (nearestLocation as any).distance = distanceKm;
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Kon je locatie niet bepalen. Controleer of je locatie toegang hebt gegeven.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      alert('Geolocatie wordt niet ondersteund door je browser.');
    }
  };

  // Helper function to calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const generateTimeSlots = (date: string) => {
    const slots = [];
    const startHour = 12; // Start om 12:00
    const endHour = 22; // Eind om 22:00
    const slotDuration = 75; // 1u15 in minuten
    
    for (let hour = startHour; hour < endHour; hour++) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      const endTime = `${(hour + 1).toString().padStart(2, '0')}:15`;
      
      // Simuleer bezette slots (voor demo doeleinden)
      const isOccupied = Math.random() < 0.3; // 30% kans dat een slot bezet is
      
      slots.push({
        time,
        endTime,
        isOccupied,
        display: `${time} - ${endTime}`
      });
    }
    
    return slots;
  };

  const handleTimeSlotSelect = (time: string) => {
    setReservationForm(prev => ({ ...prev, time, date: selectedDate }));
    setTimeSlotDialogOpen(false);
    setReservationDialogOpen(true);
  };

  const handleSubmitReservation = async () => {
    if (!reservationLocation) return;
    
    try {
      const reservationData = {
        locationId: reservationLocation.id,
        locationName: reservationLocation.name,
        customerName: `${reservationForm.firstName} ${reservationForm.lastName}`,
        customerEmail: reservationForm.email,
        customerPhone: reservationForm.phone,
        date: reservationForm.date,
        time: reservationForm.time,
        guests: reservationForm.guests,
        specialRequests: reservationForm.notes
      };
      
      const result = await apiService.submitReservation(reservationData);
      
      if (result.success) {
        // Reset form and close dialog
        setReservationForm({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          date: '',
          time: '18:00',
          guests: 2,
          notes: ''
        });
        setReservationDialogOpen(false);
        setReservationLocation(null);
        
        alert('Reservering succesvol ingediend!');
      } else {
        alert(`Fout: ${result.error}`);
      }
    } catch (error) {
      console.error('Error submitting reservation:', error);
      alert('Er is een fout opgetreden bij het indienen van de reservering.');
    }
  };

  const quickReservationOptions = [
    { label: '1 persoon', time: '18:00', guests: 1 },
    { label: '2 personen', time: '18:00', guests: 2 },
    { label: '3 personen', time: '18:00', guests: 3 },
    { label: '4 personen', time: '18:00', guests: 4 },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {showTakeaway ? (
        <TakeawayView 
          onBack={() => setShowTakeaway(false)} 
          location={selectedLocation ? {
            name: selectedLocation.name,
            address: selectedLocation.address,
            city: selectedLocation.city
          } : undefined}
        />
      ) : (
        <>
          {/* Header */}
          <AppBar position="static" elevation={0}>
            <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <RestaurantIcon sx={{ fontSize: 32, color: '#000000' }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000000' }}>
                  Poule&Poulette
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Language Switcher */}
                <ToggleButtonGroup
                  value={language}
                  exclusive
                  onChange={(e, newLanguage) => newLanguage && setLanguage(newLanguage)}
                  size="small"
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      '&.Mui-selected': {
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        '&:hover': { backgroundColor: '#333333' }
                      }
                    }
                  }}
                >
                  <ToggleButton value="nl" aria-label="Nederlands">NL</ToggleButton>
                  <ToggleButton value="fr" aria-label="Français">FR</ToggleButton>
                  <ToggleButton value="en" aria-label="English">EN</ToggleButton>
                </ToggleButtonGroup>
                
                {/* Sticky Reserveer Nu Button */}
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    if (locations.length > 0) {
                      handleLocationClick(locations[0]);
                    }
                  }}
                  sx={{ 
                    backgroundColor: '#4caf50',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    minWidth: 140,
                    height: 48,
                    '&:hover': { backgroundColor: '#45a049' }
                  }}
                >
                  {t.reserveNow}
                </Button>
                
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<TakeawayIcon />}
                  onClick={() => setShowTakeaway(true)}
                  sx={{ 
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    minWidth: 140,
                    height: 48,
                    '&:hover': { backgroundColor: '#333333' }
                  }}
                >
                  {t.takeaway}
                </Button>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Hero Section */}
          <Box sx={{
            position: 'relative',
            height: { xs: '300px', md: '400px' },
            background: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url("https://images.unsplash.com/photo-1555939594-58d7cb561ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            textAlign: 'center'
          }}>
            <Container maxWidth="lg">
              <Typography variant="h2" sx={{ 
                fontWeight: 'bold', 
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}>
                {t.heroTitle}
              </Typography>
              <Typography variant="h5" sx={{ 
                mb: 4,
                fontSize: { xs: '1.1rem', md: '1.5rem' },
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                opacity: 0.9
              }}>
                {t.heroSubtitle}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                {quickReservationOptions.map((option, index) => (
                  <Button
                    key={index}
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (locations.length > 0) {
                        setReservationForm(prev => ({ 
                          ...prev, 
                          time: option.time, 
                          guests: option.guests 
                        }));
                        handleLocationClick(locations[0]);
                      }
                    }}
                    sx={{ 
                      backgroundColor: '#4caf50',
                      color: '#ffffff',
                      fontWeight: 'bold',
                      px: 3,
                      py: 1.5,
                      fontSize: '1rem',
                      '&:hover': { backgroundColor: '#45a049' }
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Box>
            </Container>
          </Box>

          {/* Search Bar */}
          <Box sx={{ p: 4, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <TextField
                    fullWidth
                    placeholder="Stad, Postcode"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: { md: 200 } }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MyLocationIcon />}
                    onClick={handleMyLocation}
                    sx={{ height: 56 }}
                  >
                    Vind mijn locatie
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Main Content */}
          <Box sx={{ flexGrow: 1, display: 'flex', height: 'calc(100vh - 280px)', mb: 2 }}>
            {/* Location List - Hidden on mobile when map is shown */}
            {(!isMobile || !showMapOnMobile) && (
              <Box sx={{ 
                width: isMobile ? '100%' : 400, 
                borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column'
              }}>
                                {/* Scrollable List Container */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredLocations.map((location) => (
                      <Card 
                        key={location.id} 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }
                        }}
                        onClick={() => handleLocationClick(location)}
                      >
                        <CardContent sx={{ p: 0 }}>
                          <Box sx={{ display: 'flex' }}>
                            {/* Thumbnail Image */}
                            <Box sx={{
                              width: 120,
                              height: 120,
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              position: 'relative',
                              overflow: 'hidden',
                              borderRadius: '8px 0 0 8px'
                            }}>
                              <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: `url("https://images.unsplash.com/photo-1555939594-58d7cb561ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=400&q=80")`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                opacity: 0.8
                              }} />
                              <Box sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                backgroundColor: location.isOpen ? '#4caf50' : '#f44336',
                                color: '#ffffff',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                fontWeight: 'bold'
                              }}>
                                {location.isOpen ? 'OPEN' : 'GESLOTEN'}
                              </Box>
                            </Box>
                            
                            {/* Content */}
                            <Box sx={{ flex: 1, p: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {location.name}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {location.address}, {location.city}
                              </Typography>
                              
                              {/* Rating */}
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <StarIcon sx={{ fontSize: 16, color: '#ffc107', mr: 0.5 }} />
                                <Typography variant="body2" sx={{ mr: 1 }}>
                                  4.5 (127 {t.reviews})
                                </Typography>
                                <Chip 
                                  label="Nog 3 tafels vrij" 
                                  size="small" 
                                  sx={{ 
                                    backgroundColor: '#4caf50', 
                                    color: '#ffffff',
                                    fontSize: '0.7rem'
                                  }} 
                                />
                              </Box>
                              
                              {/* Services */}
                              <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                                {location.services.slice(0, 3).map((service) => (
                                  <Chip
                                    key={service}
                                    label={service}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', height: 24 }}
                                  />
                                ))}
                              </Box>
                              
                              {/* Customer Quote */}
                              <Typography variant="body2" sx={{ 
                                fontStyle: 'italic', 
                                color: '#666',
                                fontSize: '0.8rem'
                              }}>
                                "Geweldige sfeer en heerlijk eten!" - Sarah M.
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            {/* Map - Always visible on desktop, conditional on mobile */}
            {(!isMobile || showMapOnMobile) && (
              <Box sx={{ flexGrow: 1, height: '100%' }}>
                <MapContainer
                  ref={mapRef}
                  center={mapCenter}
                  zoom={mapZoom}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {locations.map((location) => (
                    <Marker
                      key={location.id}
                      position={location.coordinates}
                      eventHandlers={{
                        click: () => handleLocationClick(location),
                      }}
                    />
                  ))}
                </MapContainer>
              </Box>
            )}
          </Box>

          {/* Mobile Map Toggle */}
          {isMobile && (
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={showMapOnMobile ? <ListIcon /> : <LocationIcon />}
                onClick={() => setShowMapOnMobile(!showMapOnMobile)}
              >
                {showMapOnMobile ? 'Toon lijst' : 'Toon kaart'}
              </Button>
            </Box>
          )}

          {/* CTA Banner */}
          <Box sx={{ 
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e0e0e0',
            borderBottom: '1px solid #e0e0e0',
            py: 2,
            px: 2
          }}>
            <Container maxWidth="lg">
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                gap: 2,
                alignItems: 'center'
              }}>
                {/* Content */}
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    mb: 0.5,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: '#000000'
                  }}>
                    Proef Poule&Poulette
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 1.5, 
                    color: '#666',
                    lineHeight: 1.4,
                    fontSize: { xs: '0.85rem', md: '0.9rem' }
                  }}>
                    Traditionele kipgerechten en Belgische specialiteiten
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: { xs: 'center', md: 'flex-start' }
                  }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setShowTakeaway(true)}
                      sx={{ 
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        py: 0.75,
                        px: 2,
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: '#333333' }
                      }}
                    >
                      Takeaway
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        if (locations.length > 0) {
                          handleLocationClick(locations[0]);
                        }
                      }}
                      sx={{ 
                        borderColor: '#000000',
                        color: '#000000',
                        py: 0.75,
                        px: 2,
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        '&:hover': { 
                          backgroundColor: '#000000',
                          color: '#ffffff'
                        }
                      }}
                    >
                      Reserveren
                    </Button>
                  </Box>
                </Box>

                {/* Image */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  alignItems: 'center'
                }}>
                  <Box sx={{
                    width: { xs: '100%', md: '180px' },
                    height: { xs: '100px', md: '120px' },
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'url("https://images.unsplash.com/photo-1555939594-58d7cb561ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80")',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      opacity: 0.7
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="body1" sx={{ 
                        color: '#ffffff', 
                        fontWeight: 'bold',
                        textAlign: 'center',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                        fontSize: { xs: '0.8rem', md: '1rem' }
                      }}>
                        Poule&Poulette
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Footer */}
          <Box sx={{ 
            backgroundColor: '#f8f9fa', 
            borderTop: '1px solid #e0e0e0',
            p: 4,
            mt: 'auto'
          }}>
            <Container maxWidth="lg">
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon sx={{ fontSize: 20, color: '#000000' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000000' }}>
                    Poule&Poulette
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  © 2025 Poule&Poulette. Alle rechten voorbehouden.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Privacy
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Voorwaarden
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Contact
                  </Typography>
                </Box>
              </Box>
            </Container>
          </Box>
        </>
      )}

      {/* Location Details Dialog */}
      <Dialog 
        open={locationDialogOpen} 
        onClose={() => {
          setLocationDialogOpen(false);
          // Reset map to overview when dialog is closed
          setTimeout(() => {
            setMapCenter([50.8503, 4.3517]);
            setMapZoom(7);
          }, 300);
        }}
        maxWidth="sm"
        fullWidth
      >
        {clickedLocation && (
          <>
            <DialogTitle sx={{ fontWeight: 'bold' }}>
              {clickedLocation.name}
              {(clickedLocation as any).distance && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Dichtstbijzijnde locatie ({(clickedLocation as any).distance} km van jouw locatie)
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  {clickedLocation.address}, {clickedLocation.postcode} {clickedLocation.city}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  backgroundColor: clickedLocation.isOpen ? '#e8f5e8' : '#ffeaea',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  border: `2px solid ${clickedLocation.isOpen ? '#4caf50' : '#f44336'}`
                }}>
                  <TimeIcon sx={{ 
                    fontSize: 20, 
                    mr: 1, 
                    color: clickedLocation.isOpen ? '#2e7d32' : '#d32f2f'
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontWeight: 'bold',
                    color: clickedLocation.isOpen ? '#2e7d32' : '#d32f2f'
                  }}>
                    {clickedLocation.isOpen ? `Open tot ${clickedLocation.closingTime}` : 'Gesloten'}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <PhoneIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                  {clickedLocation.phone}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {clickedLocation.services.map((service) => (
                    <Chip
                      key={service}
                      label={service}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.8rem' }}
                    />
                  ))}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => {
                  handleReservation(clickedLocation);
                  setLocationDialogOpen(false);
                }}
                sx={{ 
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#333333' }
                }}
              >
                Reserveren
              </Button>
              <Button
                variant="outlined"
                startIcon={<TakeawayIcon />}
                onClick={() => {
                  handleTakeaway(clickedLocation);
                  setLocationDialogOpen(false);
                }}
              >
                Takeaway
              </Button>
              <Button
                onClick={() => setLocationDialogOpen(false)}
              >
                Sluiten
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Time Slot Selector Dialog */}
      <Dialog 
        open={timeSlotDialogOpen} 
        onClose={() => setTimeSlotDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 700,
            width: '100%'
          }
        }}
      >
        {reservationLocation && (
          <>
            <DialogTitle sx={{ 
              fontWeight: 'bold', 
              textAlign: 'center',
              pb: 1,
              '& .MuiTypography-root': {
                fontSize: '1rem',
                color: '#000000'
              }
            }}>
              Kies een tijdsslot - {reservationLocation.name}
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 3 }}>
              {/* Date Selector */}
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Selecteer een datum
                </Typography>
                <TextField
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    maxWidth: 200,
                    '& .MuiInputBase-input': {
                      textAlign: 'center'
                    }
                  }}
                />
              </Box>

              {/* Time Slots Grid */}
              {selectedDate && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Beschikbare tijdsslots
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
                    Elke reservering duurt 1 uur en 15 minuten
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, 
                    gap: 2 
                  }}>
                    {generateTimeSlots(selectedDate).map((slot) => (
                      <Button
                        key={slot.time}
                        variant={slot.isOccupied ? "outlined" : "contained"}
                        onClick={() => !slot.isOccupied && handleTimeSlotSelect(slot.time)}
                        disabled={slot.isOccupied}
                        sx={{
                          py: 2,
                          px: 2,
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          backgroundColor: slot.isOccupied ? '#f5f5f5' : '#000000',
                          color: slot.isOccupied ? '#999' : '#ffffff',
                          border: slot.isOccupied ? '1px solid #ddd' : 'none',
                          '&:hover': {
                            backgroundColor: slot.isOccupied ? '#f5f5f5' : '#333333'
                          },
                          '&:disabled': {
                            backgroundColor: '#f5f5f5',
                            color: '#999'
                          }
                        }}
                      >
                        {slot.display}
                        {slot.isOccupied && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                            Bezet
                          </Typography>
                        )}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                onClick={() => setTimeSlotDialogOpen(false)}
                sx={{ color: '#666' }}
              >
                Annuleren
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reservation Form Dialog */}
      <Dialog 
        open={reservationDialogOpen} 
        onClose={() => setReservationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 600,
            width: '100%'
          }
        }}
      >
        {reservationLocation && (
          <>
            <DialogTitle sx={{ 
              fontWeight: 'bold', 
              textAlign: 'center',
              pb: 1,
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#666'
              }
            }}>
              RESERVERING
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 3 }}>
              {/* Reservation Summary */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Reservering voor {reservationForm.guests} personen, vandaag om {reservationForm.time || '18:00'} uur.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Je reservering eindigt om:
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#2c2c2c', 
                  color: '#ffffff', 
                  py: 1, 
                  px: 2, 
                  borderRadius: 1,
                  display: 'inline-block',
                  fontWeight: 'bold'
                }}>
                  {reservationForm.time ? 
                    new Date(`2000-01-01T${reservationForm.time}`).getHours() + 1 + ':15 uur' : 
                    '19:15 uur'
                  }
                </Box>
              </Box>

              {/* Customer Details Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Je gegevens
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Je ontvangt een bevestiging van je reservering per e-mail.
                </Typography>

                {/* Salutation Radio Buttons */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {['Dhr.', 'Mevr.', 'Mx.', 'Familie', 'Bedrijf'].map((salutation) => (
                      <Box key={salutation} sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          id={salutation}
                          name="salutation"
                          value={salutation}
                          style={{ marginRight: '4px' }}
                        />
                        <label htmlFor={salutation} style={{ fontSize: '0.875rem' }}>
                          {salutation}
                        </label>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Name and Contact Fields */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="Voornaam"
                    value={reservationForm.firstName || ''}
                    onChange={(e) => setReservationForm({...reservationForm, firstName: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Achternaam"
                    value={reservationForm.lastName || ''}
                    onChange={(e) => setReservationForm({...reservationForm, lastName: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="E-mail"
                    type="email"
                    value={reservationForm.email}
                    onChange={(e) => setReservationForm({...reservationForm, email: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Telefoonnummer"
                    value={reservationForm.phone}
                    onChange={(e) => setReservationForm({...reservationForm, phone: e.target.value})}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          <Box sx={{ 
                            width: 16, 
                            height: 12, 
                            background: 'linear-gradient(to right, #000 33%, #ffd700 33% 66%, #ff0000 66%)',
                            borderRadius: '1px',
                            mr: 0.5
                          }} />
                          <Typography variant="caption">+32</Typography>
                        </Box>
                      ),
                    }}
                  />
                </Box>

                {/* Notes Field */}
                <TextField
                  label="Heb je belangrijke dieetwensen, allergieën of andere opmerkingen?"
                  multiline
                  rows={3}
                  value={reservationForm.notes}
                  onChange={(e) => setReservationForm({...reservationForm, notes: e.target.value})}
                  fullWidth
                  size="small"
                />
              </Box>

              {/* Marketing Opt-in */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="marketing"
                    defaultChecked
                    style={{ marginRight: '8px' }}
                  />
                  <label htmlFor="marketing" style={{ fontSize: '0.875rem' }}>
                    Mis niks van de nieuwste events, promoties en arrangementen van P&P Gent
                  </label>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                variant="contained"
                onClick={handleSubmitReservation}
                disabled={!reservationForm.firstName || !reservationForm.lastName || !reservationForm.email || !reservationForm.phone}
                fullWidth
                sx={{ 
                  backgroundColor: '#8B4513',
                  color: '#ffffff',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#A0522D' },
                  '&:disabled': { backgroundColor: '#ccc' }
                }}
              >
                Reserveer nu
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default GuestReservationApp;
